import { useCallback, useContext } from "react"
import { DeepReadonly } from "ts-essentials"
import { Compare } from "@mui/icons-material"
import InstrumentSelector from "connected-components/instrument-selector"
import { IsoDate } from "helpers/types/dates"
import { InstrumentMetadata } from "helpers/types/instruments/metadata"
import { RECENT_INSTRUMENT_SEARCH_TYPES } from "resources/instruments"
import { IDashboardContext } from "connected-components/dashboard/context/types"
import useOpenUnifiedSearch from "connected-components/dashboard/hooks/use-open-unified-search"
import { MODE_SELECTOR_OPTIONS, SELECTED_MODE_VALUES } from "../../helpers"
import { useAnalytics } from "connected-components/analytics"
import { DASHBOARD_EVENTS } from "../../analytics"
import type { InstrumentSelectionData } from "../../analytics"
import ComparisonMenu from "../comparison-menu"
import { RULES } from "connected-components/compare/position-sets"
import { PositionSetWeightingRule, RuleWeightingStrategyType, WeightingGroupingType } from "helpers/types/instruments/selected"

export default function DashboardTopBarCompare({ context, comparisonInstrumentMetadata, adjustedStartDate }: DeepReadonly<DashboardTopBarCompareProps>) {
  const analytics = useAnalytics()

  const {
    instrument: mainInstrument,
    comparisonInstrument,
    secondInstrument,
    setComparisonInstrument,
    setSelectedMode,
    endDate,
    setIsEndVsStartDateComparison,
    isEndVsStartDateComparison,
    isOptimizePanelExpanded,
    setIsOptimizePanelExpanded,
  } = useContext(context)

  const onComparisonInstrumentSelection = useCallback(({ id, name, type, searchType }: InstrumentSelectionData) => {
    analytics.track(DASHBOARD_EVENTS.COMPARISON_SEARCH_SELECTION, { instrument: { id, name, type }, searchType })
  }, [analytics])

  const openSearch = useOpenUnifiedSearch(context, setComparisonInstrument, RECENT_INSTRUMENT_SEARCH_TYPES.DASHBOARD, mainInstrument, true, false, onComparisonInstrumentSelection)

  const handleOpenComparisonSearch = useCallback(() => {
    analytics.track(DASHBOARD_EVENTS.COMPARISON_SEARCH_OPENED)

    openSearch()
  }, [analytics, openSearch])

  const handleSetIsEndVsStartDateComparison = useCallback(() => {
    setIsEndVsStartDateComparison(true)
    setComparisonInstrument(mainInstrument)
    setSelectedMode(MODE_SELECTOR_OPTIONS[SELECTED_MODE_VALUES.endVsStart].id)
  }, [setIsEndVsStartDateComparison, setComparisonInstrument, mainInstrument, setSelectedMode])

  const handleSetWeightingStrategy = useCallback((weightingStrategyType: RuleWeightingStrategyType) => {
    if (!mainInstrument || mainInstrument.type === undefined) {
      throw new Error("Cannot set weighting strategy for this instrument type")
    }

    let weightingRule: PositionSetWeightingRule
    if (weightingStrategyType === "equalWeighting") {
      weightingRule = { equalWeighting: { groupBy: WeightingGroupingType.LONG_SHORT } }
    } else if (weightingStrategyType === "idioWeighting") {
      weightingRule = { idioWeighting: true }
    } else {
      weightingStrategyType satisfies never
      throw new Error(`Unrecognized weighting strategy type: ${weightingStrategyType}`)
    }

    setComparisonInstrument({
      type: RULES,
      id: undefined,
      rulesConfiguration: {
        source: { positionSet: mainInstrument },
        rules: [
          { weighting: weightingRule },
        ],
      },
    })
  }, [mainInstrument, setComparisonInstrument])

  const handleComparisonRemove = useCallback(() => {
    const comparisonId = comparisonInstrument && comparisonInstrument.type !== RULES ? comparisonInstrument.id : undefined
    analytics.track(DASHBOARD_EVENTS.COMPARISON_REMOVED, {
      comparisonInstrumentId: comparisonId,
      comparisonInstrumentName: comparisonInstrumentMetadata?.name,
    })
    setIsEndVsStartDateComparison(false)
    setComparisonInstrument(null)
    setSelectedMode(secondInstrument ? "rebalanced" : "original")
  }, [setComparisonInstrument, comparisonInstrument, comparisonInstrumentMetadata, setIsEndVsStartDateComparison, setSelectedMode, secondInstrument, analytics])

  if (!comparisonInstrument) {
    return <ComparisonMenu
      onInstrumentClick={handleOpenComparisonSearch}
      onDateComparisonClick={handleSetIsEndVsStartDateComparison}
      isOptimizePanelExpanded={isOptimizePanelExpanded}
      setIsOptimizePanelExpanded={setIsOptimizePanelExpanded}
      onSetWeightStrategy={handleSetWeightingStrategy}
    />
  }

  if (!comparisonInstrumentMetadata) {
    return null
  }

  const isWeightStrategyActive = comparisonInstrument?.type === RULES

  return (
    <InstrumentSelector
      icon={<Compare />}
      selectedInstrumentMetadata={comparisonInstrumentMetadata}
      openSearch={openSearch}
      onClear={handleComparisonRemove}
      disabled={isWeightStrategyActive}
      {...(isEndVsStartDateComparison ? {
        startDate: adjustedStartDate,
        endDate: endDate,
      } : {})}
    />
  )
}

interface DashboardTopBarCompareProps {
  context: IDashboardContext
  adjustedStartDate?: IsoDate
  comparisonInstrumentMetadata?: InstrumentMetadata
}
